import React, { useEffect, useRef, FC } from 'react';
import { PopupProps } from './popup.types';
import { StyledPopupDiv } from './popup.styles';
import { shouldIgnoreClickOutside, shouldIgnoreFocusOutside } from '@marriott/mi-ui-library';

const Popup: FC<PopupProps> = ({
  className,
  show,
  children,
  handleBlur,
  popupOpenState,
  setPopupOpenState,
  role,
  labelledBy,
  width,
  popupId,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // close popup on desktop if tab focus is out of popup or user clicks outside the popup
    if (handleBlur && popupOpenState && setPopupOpenState) {
      const popupElem = popupRef.current;
      if (popupElem && popupOpenState) {
        // click event handler
        const clickListener = (event: Event): void => {
          const target = event.target as Element;
          // Ignore clicks on external modals (privacy popup, Adobe experiments, etc.)
          if (shouldIgnoreClickOutside(target)) {
            return;
          }
          if (!popupElem.contains(target) && !popupElem.contains(document.activeElement)) {
            setPopupOpenState(false);
          }
        };

        // focus out handler
        const focusoutListener = (event: FocusEvent): void => {
          const relatedTarget = event.relatedTarget as Element;
          // Ignore focus changes to external modals (privacy popup, Adobe experiments, etc.)
          if (shouldIgnoreFocusOutside(relatedTarget)) {
            return;
          }
          if (
            event.relatedTarget &&
            !popupElem.contains(relatedTarget) &&
            !popupElem.contains(document.activeElement) &&
            !popupElem.contains(event.target as Element)
          ) {
            setPopupOpenState(false);
          }
        };

        // handling tab or esc keypress
        const handleKeyPress = (event: KeyboardEvent): void => {
          // handle tab keypress
          if (event.key === 'Tab') {
            const buttons = Array.from(popupElem?.querySelectorAll('button, input[type="radio"]')); // only Reading from DOM
            const tabbableButtons = buttons.filter(button => {
              return button.clientHeight > 0;
            });
            const firstTabbableButton = tabbableButtons[0];
            const lastTabbableButton = tabbableButtons[tabbableButtons.length - 1];
            const isTabbingOut = !event.shiftKey && event.target === lastTabbableButton;
            if (isTabbingOut) {
              setPopupOpenState(false);
            }
            const isShiftTabbingOut = event.shiftKey && event.target === firstTabbableButton;
            if (isShiftTabbingOut) {
              setPopupOpenState(false);
            }
          }

          // close popup with esc keypress
          if (event.key === 'Escape') {
            setPopupOpenState(false);
          }
        };

        document.addEventListener('click', clickListener);
        popupElem.addEventListener('focusout', focusoutListener);
        document.addEventListener('keydown', handleKeyPress);

        return (): void => {
          document.removeEventListener('click', clickListener);
          popupElem.removeEventListener('focusout', focusoutListener);
          document.removeEventListener('keydown', handleKeyPress);
        };
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return (): void => {};
  }, [handleBlur, popupOpenState, setPopupOpenState]);

  return (
    <StyledPopupDiv
      data-component-name="a-shop-Popup"
      data-testid="shop-Popup"
      className={className}
      show={show}
      width={width}
      ref={popupRef}
      role={role}
      aria-labelledby={labelledBy}
      id={popupId}
    >
      <div id={labelledBy}>{children}</div>
    </StyledPopupDiv>
  );
};

export default Popup;
